import { useFormikContext } from 'formik';
import { useEffect, useCallback, useState } from 'react';
import { IDropdownOption } from '../../../../../../components/Dropdown/IDropdown';
import MaterialDataCheckboxes from '../MaterialDataCheckboxes/MaterialDataCheckboxes';
import MaterialDataDropdowns from '../MaterialDataDropdowns/MaterialDataDropdowns';
import { IFormikForm } from '../ModalMaterial/FormikMaterial';
import { castToIDropdownOptionArray } from './MaterialCategoryFactory';
import Natural from './Natural';
import { get } from 'lodash';
import { IPhysicalDataResponse } from '../../../../../api/IApiResponse';
import { useAppSelector } from '../../../../../../reduxService/hooks';
import { selectProjectInfo } from '../../../../../store/projectInfo/projectInfoSlice';
import { UnitOfMeasure } from '../../../../../enum/project';

export const thicknessException = 0.3; //Only this value required another approx

export const Reno = () => {
    const { values, setFieldValue } = useFormikContext<IFormikForm>();
    const [optionThickness, setOptionsThickness] = useState<IDropdownOption[]>([]);
    const [optionsCu, setOptionsCu] = useState<IDropdownOption[]>([]);
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;
    const [optionsRock, setOptionsRock] = useState<IDropdownOption[]>([]);
    const [localThickness, setThickness] = useState<number>(values[values.sectorChannel].thickness || 0);
    const [cu, setCu] = useState<number>(values[values.sectorChannel].cu || 0);
    const [rock, setRock] = useState<number>(values[values.sectorChannel].rockD50 || 0);
    const handleInputChange = (e: any) => {};

    let physicalData: IPhysicalDataResponse[] = get(values, `${values.sectorChannel}.material.physicalData`, []);

    const physicalDataCu = physicalData!.find(pd => !!pd.cu);

    useEffect(() => {
        if (!!physicalData && physicalData?.length > 0) {
            let thicknessOptions = castToIDropdownOptionArray(physicalData!, 'thickness', isImperial);
            let filteredPhysicalData = physicalData!.filter(data => localThickness == data.thickness);
            let rockOptions = castToIDropdownOptionArray(filteredPhysicalData, 'rockD50', isImperial);
            let cuOptions = castToIDropdownOptionArray(filteredPhysicalData, 'cu', isImperial);
            if (isImperial) {
                thicknessOptions = thicknessOptions.map(x => {
                    if (x.value === thicknessException) {
                        x.label = Math.ceil(parseFloat(x.label)).toString();
                    } else {
                        x.label = Math.floor(parseFloat(x.label)).toString();
                    }
                    return x;
                });
                rockOptions = rockOptions.map(x => {
                    x.label = (Math.ceil(parseFloat(x.label) * 10) / 10).toString();
                    return x;
                });
            }
            setOptionsThickness(thicknessOptions);
            setOptionsRock(rockOptions);
            setOptionsCu(cuOptions);
        }
    }, [physicalData]);

    const setValueForThickness = useCallback(
        (value: number) => {
            let filteredPhysicalData: any[] = physicalData!.filter(data => value == data.thickness);
            let rockOptions = castToIDropdownOptionArray(filteredPhysicalData, 'rockD50', isImperial);
            let cuOptions = castToIDropdownOptionArray(filteredPhysicalData, 'cu', isImperial);
            if (isImperial) {
                rockOptions = rockOptions.map(x => {
                    x.label = (Math.ceil(parseFloat(x.label) * 10) / 10).toString();
                    return x;
                });
            }
            setOptionsRock(rockOptions);
            setOptionsCu(cuOptions);
            setThickness(value);
            setFieldValue(`${values.sectorChannel}.thickness`, value);
            let selectedPhysicalData = undefined;
            if (!!physicalData && physicalData[0].cu) selectedPhysicalData = physicalData && physicalData?.find(data => value === data.thickness && rock === data.rockD50 && cu === data.cu);
            else {
                selectedPhysicalData = physicalData && physicalData?.find(data => value === data.thickness && rock === data.rockD50);
            }

            if (!!selectedPhysicalData && selectedPhysicalData.id) {
                setFieldValue(`${values.sectorChannel}.combinationPD`, selectedPhysicalData.id);
                setFieldValue(`${values.sectorChannel}.allowShearStress`, selectedPhysicalData.allowShearStress);
                setFieldValue(`${values.sectorChannel}.roughness`, selectedPhysicalData.roughness);
            }
        },
        [physicalData, cu, rock],
    );

    const setValueForRock = useCallback(
        (value: number) => {
            let filteredPhysicalData = physicalData!.filter(data => value == data.rockD50);
            let cuOptions = castToIDropdownOptionArray(filteredPhysicalData, 'cu', isImperial);
            setOptionsCu(cuOptions);
            setRock(value);
            setFieldValue(`${values.sectorChannel}.rockD50`, value);
            let selectedPhysicalData = undefined;

            if (!!physicalData && physicalData[0].cu) selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && value === data.rockD50 && cu === data.cu);
            else {
                selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && value === data.rockD50);
            }
            if (cuOptions.length === 1) {
                setCu(cuOptions[0].value as number);
                if (!!physicalData && physicalData[0].cu)
                    selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && value === data.rockD50 && cuOptions[0].value === data.cu);
            }

            if (!!selectedPhysicalData && selectedPhysicalData.id) {
                setFieldValue(`${values.sectorChannel}.combinationPD`, selectedPhysicalData.id);
                setFieldValue(`${values.sectorChannel}.allowShearStress`, selectedPhysicalData.allowShearStress);
                setFieldValue(`${values.sectorChannel}.roughness`, selectedPhysicalData.roughness);
            }
        },
        [physicalData, localThickness, cu],
    );

    const setValueForCu = useCallback(
        (value: number) => {
            setFieldValue(`${values.sectorChannel}.cu`, value);
            setCu(value);
            let selectedPhysicalData = undefined;
            selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && rock === data.rockD50 && value === data.cu);

            if (!!selectedPhysicalData && selectedPhysicalData.id) {
                setFieldValue(`${values.sectorChannel}.combinationPD`, selectedPhysicalData.id);
                setFieldValue(`${values.sectorChannel}.allowShearStress`, selectedPhysicalData.allowShearStress);
                setFieldValue(`${values.sectorChannel}.roughness`, selectedPhysicalData.roughness);
            }
        },
        [physicalData, localThickness, rock],
    );
    const handleDropdownChange = (e: any) => {
        let target = e.target;
        let dropdownName = target.id;
        let value = parseFloat(target.value);
        switch (dropdownName) {
            case 'thickness':
                setValueForThickness(value);
                break;
            case 'rockD50':
                setValueForRock(value);
                break;
            default:
                setValueForCu(value);
                break;
        }
    };

    return (
        <>
            <MaterialDataCheckboxes
                onChange={handleInputChange}
                isGeotextileFilterVisible
                isXTiesVisible={values[values.sectorChannel].material?.isXties}
                isCastoroVisible
                isAllowedChangeCheckbox={!!values[values.sectorChannel].material?.isAllowedChangeCheckbox ? values[values.sectorChannel].material!.isAllowedChangeCheckbox! : false}
            />
            <MaterialDataDropdowns
                onChange={handleDropdownChange}
                optionsThickness={optionThickness}
                defaultThickness={localThickness}
                optionsRock={optionsRock}
                defaultRock={rock}
                optionsCu={!!physicalDataCu ? optionsCu : undefined}
                defaultCu={cu}
            />
            <Natural hideForALl />
        </>
    );
};
